import { Flex, Grid, GridItem, Text } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/react'
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  Calendar,
  Voicemail,
} from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { TextButton } from '~/components/shared/TextButton'
import { ApplicationRoutes } from '~/hooks/useRouting'
import {
  CallDetailsRow,
  CallDetailsRowItem,
} from '~/pages/Calls/CallDetails/CallDetailsRow'
import { ContactInfoCard } from '~/pages/Calls/CallDetails/ContactInfoCard'
import type { MargoSummaryViewModel } from '~/pages/Calls/CallDetails/MargoSummary/MargoSummary.types'
import { MargoSummaryCard } from '~/pages/Calls/CallDetails/MargoSummary/MargoSummaryCard'
import { CallTranscriptSummarySection } from '~/pages/Calls/CallDetails/sections/CallTranscriptSummarySection'
import { OverallSentimentSection } from '~/pages/Calls/CallDetails/sections/OverallSentimentSection'
import {
  CallTarget,
  getCallTargetData,
} from '~/pages/Calls/data/CallDetailData'
import { hasValue } from '~/utils/common'
import { formatSeconds } from '~/utils/formatter'

import { formatDateString, formatTimeString } from '@mangovoice/core'
import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import {
  ApiError,
  CallDirection,
  CallInstance,
  VoicemailInstance,
} from '@mangovoice/types'

import { BouncingEllipsis } from './BouncingEllipsis'
import { getTranscriptSummaryNotAvailable } from './CallDetailsAnalyticsContent'
import { VoicemailTranscriptSection } from './sections/VoicemailTranscriptSection'

interface CallDetailsOverviewTabProps {
  callInstance?: CallInstance
  margoSummary?: MargoSummaryViewModel
  transcriptSummary?: string
  answerTimestampInMs?: number
  overallCustomerScore?: number
  overallAgentScore?: number
  isLoadingTranscripts?: boolean
  voicemail?: VoicemailInstance
  voicemailError?: ApiError
  isVoicemailLoading?: boolean
}

export const CallDetailsOverviewTab = ({
  callInstance,
  margoSummary,
  transcriptSummary,
  answerTimestampInMs,
  overallCustomerScore,
  overallAgentScore,
  isLoadingTranscripts,
  voicemail,
  voicemailError,
  isVoicemailLoading,
}: CallDetailsOverviewTabProps) => {
  const { t } = useTranslation()

  let outcomeIcon: React.ComponentType<any> | undefined
  switch (callInstance?.direction) {
    case CallDirection.Inbound:
      outcomeIcon = ArrowDownLeft
      break
    case CallDirection.Outbound:
      outcomeIcon = ArrowUpRight
      break
    case CallDirection.Internal:
      outcomeIcon = ArrowLeftRight
      break
  }
  const isTranscriptionInProgress =
    callInstance?.transcription_status === 'IN_PROGRESS'
  const transcriptSummaryNotAvailable = getTranscriptSummaryNotAvailable(t)
  const hasTranscript =
    transcriptSummary && transcriptSummary !== transcriptSummaryNotAvailable
  const hasSentiment =
    overallCustomerScore !== undefined || overallAgentScore !== undefined

  const toTargetData = callInstance
    ? getCallTargetData(callInstance, CallTarget.To)
    : undefined
  const fromTargetData = callInstance
    ? getCallTargetData(callInstance, CallTarget.From)
    : undefined

  const navigate = useNavigate()

  const hasVoicemail = hasValue(callInstance?.voicemail_id)
  const handleVoicemailClick = () => {
    if (!callInstance) return
    navigate(`${ApplicationRoutes.VOICEMAIL}/${callInstance.voicemail_id}`)
  }

  const isCallInProgress = callInstance && !callInstance.ended_at

  return (
    <Flex flexDirection="column" gap="1.25rem" width="100%">
      {margoSummary && <MargoSummaryCard summary={margoSummary} />}
      {margoSummary && hasSentiment && (
        <CallDetailsRow>
          <OverallSentimentSection
            overallCustomerScore={overallCustomerScore ?? 0}
            overallAgentScore={overallAgentScore ?? 0}
          />
        </CallDetailsRow>
      )}
      {!margoSummary && (hasTranscript || isTranscriptionInProgress) && (
        <CallDetailsRow>
          <CallTranscriptSummarySection
            callInstance={callInstance}
            isLoading={isLoadingTranscripts ?? false}
            summaryText={transcriptSummary}
            overallCustomerScore={overallCustomerScore ?? 0}
            overallAgentScore={overallAgentScore ?? 0}
            voicemail={voicemail}
            voicemailError={voicemailError}
            isVoicemailLoading={isVoicemailLoading}
            onVoicemailClick={handleVoicemailClick}
          />
        </CallDetailsRow>
      )}
      {((margoSummary && hasVoicemail) ||
        (!hasTranscript && !isTranscriptionInProgress && hasVoicemail)) && (
        <CallDetailsRow>
          <VoicemailTranscriptSection
            voicemail={voicemail}
            voicemailError={voicemailError}
            isVoicemailLoading={isVoicemailLoading}
            onVoicemailClick={handleVoicemailClick}
            isToolTip={false}
          />
        </CallDetailsRow>
      )}
      <CallDetailsRow>
        {fromTargetData && (
          <ContactInfoCard
            callTarget={CallTarget.From}
            title={fromTargetData.titleValue}
            subtitle={fromTargetData.subtitleValue}
            contact={fromTargetData.contact}
            number={fromTargetData.number}
            isExtension={fromTargetData.isExtension}
            location={fromTargetData.location}
            locationIsTitle={fromTargetData.locationIsTitle}
          />
        )}
        {toTargetData && (
          <ContactInfoCard
            callTarget={CallTarget.To}
            title={toTargetData.titleValue}
            subtitle={toTargetData.subtitleValue}
            contact={toTargetData.contact}
            number={toTargetData.number}
            isExtension={toTargetData.isExtension}
            location={toTargetData.location}
            locationIsTitle={toTargetData.locationIsTitle}
          />
        )}
      </CallDetailsRow>
      <CallDetailsRow>
        <CallDetailsRowItem>
          <Text {...textStyles[textStyleKeys.Body1_Bold]}>
            {t('callDetails.callTimeline')}
          </Text>
          <Flex
            data-testid="call-details-row"
            justifyContent="space-between"
            alignItems="center"
            alignSelf="stretch"
          >
            <Flex
              gap="1rem"
              alignItems="center"
              data-testid="call-details-date-direction-label-container"
            >
              <Flex alignItems="center" gap="0.5rem">
                <Icon boxSize="1rem">
                  <Calendar size="100%" />
                </Icon>
                <Text
                  {...textStyles[textStyleKeys.Body2_Semibold]}
                  lineHeight="1.25rem"
                >
                  {formatDateString(callInstance!.started_at)}
                </Text>
              </Flex>
              <Flex alignItems="center" gap="0.25rem">
                <Icon
                  boxSize="1rem"
                  css={{
                    color: isCallInProgress
                      ? 'brand.fg'
                      : callInstance!.is_missed
                        ? 'red.solid'
                        : 'initial',
                  }}
                >
                  {outcomeIcon &&
                    React.createElement(outcomeIcon, {
                      size: '100%',
                    })}
                </Icon>
                <Text
                  {...textStyles[textStyleKeys.Body2_Semibold]}
                  lineHeight="1.25rem"
                  color={
                    isCallInProgress
                      ? 'brand.fg'
                      : callInstance!.is_missed
                        ? 'red.solid'
                        : 'initial'
                  }
                >
                  {t(`callDetailData.direction.${callInstance!.direction}`) +
                    ', '}
                  {isCallInProgress
                    ? t('callDetailData.inProgress')
                    : callInstance!.is_missed
                      ? t('callDetailsOverviewTab.missed')
                      : t('callDetailsOverviewTab.answered')}
                </Text>
              </Flex>
            </Flex>
            <Flex>
              {hasVoicemail && !transcriptSummary && (
                <TextButton
                  leftIcon={Voicemail}
                  onLinkClick={handleVoicemailClick}
                >
                  {t('callDetailsOverviewTab.textButton')}
                </TextButton>
              )}
            </Flex>
          </Flex>
          <Grid templateColumns="repeat(4, 1fr)" gap="0.5rem" w="full">
            <GridItem
              data-testid="call-details-block"
              gridColumn="span 1"
              height="3.5rem"
              py="0.5rem"
              px="0.75rem"
              borderRadius="0.5rem"
              bg="bg.muted"
            >
              <Text {...textStyles[textStyleKeys.Body1_Bold]}>
                {formatTimeString(callInstance!.started_at)}
              </Text>
              <Text {...textStyles[textStyleKeys.Body3]} lineHeight="1rem">
                {t('callDetails.started')}
              </Text>
            </GridItem>
            {answerTimestampInMs && (
              <GridItem
                data-testid="call-details-block"
                gridColumn="span 1"
                height="3.5rem"
                py="0.5rem"
                px="0.75rem"
                borderRadius="0.5rem"
                bg="bg.muted"
              >
                <Text {...textStyles[textStyleKeys.Body1_Bold]}>
                  {formatSeconds(answerTimestampInMs / 1000)}
                </Text>
                <Text {...textStyles[textStyleKeys.Body3]} lineHeight="1rem">
                  {t('callDetails.answerTime')}
                </Text>
              </GridItem>
            )}
            <GridItem
              data-testid="call-details-block"
              display="flex"
              gridColumn={answerTimestampInMs ? 'span 1' : 'span 2'}
              height="3.5rem"
              py="0.5rem"
              px="0.75rem"
              flexDirection="column"
              justifyContent="center"
              alignItems={answerTimestampInMs ? 'flex-start' : 'center'}
              borderRadius="0.5rem"
              bg="bg.muted"
            >
              <Text {...textStyles[textStyleKeys.Body1_Bold]}>
                {isCallInProgress ? (
                  <BouncingEllipsis />
                ) : (
                  formatSeconds(callInstance!.duration_in_seconds)
                )}
              </Text>
              <Text {...textStyles[textStyleKeys.Body3]} lineHeight="1rem">
                {t('callDetails.callDuration')}
              </Text>
            </GridItem>
            <GridItem
              data-testid="call-details-block"
              gridColumn="span 1"
              height="3.5rem"
              py="0.5rem"
              px="0.75rem"
              borderRadius="0.5rem"
              bg="bg.muted"
            >
              <Text {...textStyles[textStyleKeys.Body1_Bold]}>
                {callInstance?.ended_at ? (
                  formatTimeString(callInstance.ended_at)
                ) : (
                  <BouncingEllipsis />
                )}
              </Text>
              <Text {...textStyles[textStyleKeys.Body3]} lineHeight="1rem">
                {t('callDetails.ended')}
              </Text>
            </GridItem>
          </Grid>
        </CallDetailsRowItem>
      </CallDetailsRow>
      {callInstance?.call_flow_entries &&
        callInstance.call_flow_entries.length > 0 && (
          <CallDetailsRow>
            <CallDetailsRowItem>
              <Text {...textStyles[textStyleKeys.Body1_Bold]}>
                {t('callDetails.callFlow')}
              </Text>
              <Flex
                data-testid="call-flow-data-container"
                flexDirection="column"
                alignItems="flex-start"
                alignSelf="stretch"
              >
                {
                  //TODO: Fix typing on call_flow_entries
                  callInstance!.call_flow_entries.map((entry, index) => {
                    return (
                      <Text key={index} {...textStyles[textStyleKeys.Body1]}>
                        {entry.label}: {entry.value}
                      </Text>
                    )
                  })
                }
              </Flex>
            </CallDetailsRowItem>
          </CallDetailsRow>
        )}
    </Flex>
  )
}
